import React, {useEffect, useRef, useState} from 'react';
import { useToggle } from 'react-use';
import StudentProfileContainer from './StudentProfileContainer';
import { hot } from 'react-hot-loader/root';
import StickyFooterLayout from 'common/StickyFooterLayout';
import ProfileFooter from '../ProfileFooter';
import PropTypes from 'prop-types';
import useStarfishAPI from 'hooks/useStarfishAPI';
import { urlParams } from 'utils/request';
import { assign, cloneDeep } from 'lodash';
import { SFEntityID, SFNotification } from 'utils/types';
import Toast from 'common/Toast';
import ConfirmDialog from 'common/ConfirmDialog';
import { withNotification } from 'common/withNotification';
import css from '../staff/App.scss';
import { isStringOverCharLimit } from '../profileValidation';

/**
 * @param {object} props - see propTypes
 * @returns {JSX.Element} the student's profile
 */
function App(props) {
    const [userProfile, setUserProfile] = useState(null);
    const [toastText, setToastText] = useState("");
    const [originalUserProfile, setOriginalUserProfile] = useState({});
    const [profileResponse, callProfileAPI] = useStarfishAPI();
    const [saveResponse, callSaveAPI] = useStarfishAPI();
    const [showToast, toggleToast] = useToggle(false);
    const [showCancelDialog, toggleCancelDialog] = useToggle(false);

    const studentContentRef = useRef();

    useEffect(() => {
        callProfileAPI('../student/getStudentMyProfile.html', {
            method: 'POST',
            body: urlParams({ studentId: props.userId })
        })
    }, [props.userId]);
    
    useEffect(() => {
        if (profileResponse.success && profileResponse.data.result) {
            setUserProfile(profileResponse.data.result[0]);
            setOriginalUserProfile(profileResponse.data.result[0]);
        }
    }, [profileResponse]);
    
    const setValue = (values) =>
        setUserProfile(assign({}, userProfile, values));
    
    const onCancel = () => { 
        setUserProfile(originalUserProfile);
        toggleCancelDialog(false);
        setToastText('Your changes have been cleared.');
        toggleToast(true);
    };
    
    const onSave = async () => {
        const userProfileClone = cloneDeep(userProfile);
        delete userProfileClone.lastLoginTime;
        delete userProfileClone.uuid;

        if (!userProfileClone.sendAlertTime) {
            userProfileClone.sendAlertTime = 15
        }

        await callSaveAPI('../student/saveStudentMyProfile.html', {
            method: 'POST',
            body: urlParams({
                ...userProfileClone,
                partyId: props.userId
            })
        });
    };
    
    useEffect(() => {
        if (saveResponse.success) {
            setOriginalUserProfile(userProfile);
            setToastText('Your changes have been saved.');
            toggleToast(true);
        }
        
        if (saveResponse.error) {
            props.notification.error(saveResponse.error);
        }
    }, [saveResponse]);
    
    const getToast = () => {
        if (!showToast) return null;
        return <Toast className={css['sticky-footer-toast']}
                      message={toastText}
                      onTimeout={() => toggleToast(false)} />
    };

    // Don't show an error or prevent saving if the feature is disabled
    const invalidPronouns = () => props.showPronouns
        && props.allowEditPronouns
        && isStringOverCharLimit(userProfile.pronouns, 25);
    
    const getContent = () => (
        <>
            <StudentProfileContainer {...props}
                                     invalidPronouns={invalidPronouns()}
                                     originalProfile={originalUserProfile}
                                     profile={userProfile}
                                     studentContentRef={studentContentRef}
                                     setValue={setValue} />
            {getToast()}
            <ConfirmDialog confirmText="Clear Changes"
                           cancelText="Cancel"
                           headerText="Are you sure you want to clear all changes?" 
                           descriptionText="You will not be able to recover any changes you made to your profile."
                           isOpen={showCancelDialog}
                           onClose={() => toggleCancelDialog(false)}
                           onConfirm={onCancel} />
        </>
    );
    
    const getFooter = () => (
        <ProfileFooter disableSave={invalidPronouns()}
                       onCancel={() => toggleCancelDialog(true)}
                       onSave={onSave}
                       saving={saveResponse.pending} />
    );
    
    if (!userProfile || profileResponse.pending) return <div>Loading...</div>;
    return <StickyFooterLayout content={getContent()} 
                               footer={getFooter()}
                               ref={studentContentRef} />;
}

App.defaultProps = {
    allowEditPronouns: false,
    allowPhotoUpload: false,
    allowPreferredEmail: false,
    isIE: false,
    photoAcceptTypes: '.jpeg, .jpg, .png',
    showMobileEmailInfo: false,
    showNotificationsTab: false,
    showPronouns: false,
    textMessagingEnabled: false,
    weekendReminderEmailSettingEnabled: false
};

App.propTypes = {
    allowEditPronouns: PropTypes.bool,
    allowPhotoUpload: PropTypes.bool,
    allowPreferredEmail: PropTypes.bool,
    helpDeskInformation: PropTypes.string,
    isIE: PropTypes.bool,
    mobileEmailInfoLink: PropTypes.string,
    notification: SFNotification.isRequired,
    photoAcceptTypes: PropTypes.string,
    showMobileEmailInfo: PropTypes.bool,
    showNotificationsTab: PropTypes.bool,
    showPronouns: PropTypes.bool,
    supportedCountries: PropTypes.string,
    textMessagingEnabled: PropTypes.bool,
    userId: SFEntityID.isRequired,
    validatePhoto: PropTypes.func.isRequired,
    weekendReminderEmailSettingEnabled: PropTypes.bool
};

export default hot(withNotification(App));
